import { injectStyle, createPageShell } from "./style.js"

const css = `
.version-list {
    padding: 16px 24px;
    line-height: 1.8;
}

.version-list a {
    color: light-dark(#0969da, #58a6ff);
    text-decoration: none;
}

.version-list a:hover {
    text-decoration: underline;
}
`

interface VersionListOptions {
    modulePath: string
    versions: string[]
}

// semverCompare compares two Go semver strings following golang.org/mod/semver.
// Returns negative if a < b, positive if a > b, zero if equal.
function semverCompare(a: string, b: string): number {
    const pa = parseSemver(a)
    const pb = parseSemver(b)
    if (pa === null || pb === null) {
        // Non-semver strings sort after valid semver, alphabetically among themselves.
        if (pa === null && pb === null) return a < b ? -1 : a > b ? 1 : 0
        return pa === null ? 1 : -1
    }
    let c = compareInt(pa.major, pb.major)
    if (c !== 0) return c
    c = compareInt(pa.minor, pb.minor)
    if (c !== 0) return c
    c = compareInt(pa.patch, pb.patch)
    if (c !== 0) return c
    return comparePrerelease(pa.prerelease, pb.prerelease)
}

interface Semver {
    major: number
    minor: number
    patch: number
    prerelease: string
}

function parseSemver(v: string): Semver | null {
    if (!v.startsWith("v")) return null
    v = v.slice(1)
    // Strip +build metadata.
    const plus = v.indexOf("+")
    if (plus !== -1) v = v.slice(0, plus)
    // Split prerelease.
    let prerelease = ""
    const hyphen = v.indexOf("-")
    if (hyphen !== -1) {
        prerelease = v.slice(hyphen + 1)
        v = v.slice(0, hyphen)
    }
    const parts = v.split(".")
    if (parts.length !== 3) return null
    const nums = parts.map(Number)
    if (nums.some(n => !Number.isInteger(n) || n < 0)) return null
    return { major: nums[0], minor: nums[1], patch: nums[2], prerelease }
}

function compareInt(a: number, b: number): number {
    return a - b
}

function comparePrerelease(a: string, b: string): number {
    // No prerelease > has prerelease (v1.0.0 > v1.0.0-rc.1).
    if (a === "" && b === "") return 0
    if (a === "") return 1
    if (b === "") return -1
    const aIds = a.split(".")
    const bIds = b.split(".")
    const n = Math.min(aIds.length, bIds.length)
    for (let i = 0; i < n; i++) {
        const ai = aIds[i]
        const bi = bIds[i]
        const aNum = isNumeric(ai) ? Number(ai) : null
        const bNum = isNumeric(bi) ? Number(bi) : null
        // Numeric identifiers sort before string identifiers.
        if (aNum !== null && bNum !== null) {
            if (aNum !== bNum) return aNum - bNum
        } else if (aNum !== null) {
            return -1
        } else if (bNum !== null) {
            return 1
        } else {
            if (ai < bi) return -1
            if (ai > bi) return 1
        }
    }
    return aIds.length - bIds.length
}

function isNumeric(s: string): boolean {
    return /^(?:0|[1-9]\d*)$/.exec(s) !== null
}

export function createVersionList({ modulePath, versions }: VersionListOptions): HTMLElement {
    injectStyle("version-list", css)

    const headerEl = document.createElement("span")
    headerEl.textContent = modulePath

    const { container, content } = createPageShell({ header: headerEl })

    const list = document.createElement("div")
    list.className = "version-list"

    const ul = document.createElement("ul")
    ul.style.listStyle = "none"
    ul.style.padding = "0"

    // Show versions sorted newest first.
    const sorted = [...versions].sort((a, b) => semverCompare(b, a))
    for (const version of sorted) {
        const li = document.createElement("li")
        const a = document.createElement("a")
        a.href = `/${modulePath}@${version}/`
        a.textContent = version
        li.appendChild(a)
        ul.appendChild(li)
    }

    list.appendChild(ul)
    content.appendChild(list)
    return container
}
